import cx from 'classnames';
import {QUESTION_TYPES} from 'client/constants';
import React, {RefObject, useCallback, useContext, useEffect} from 'react';

import {copyToClipboard} from '@albert-io/atomic';
import {QuestionModelV3} from '@albert-io/models';
import appActions from 'client/AppActions';
import {callAction} from 'client/framework';
import {
  GlobalNavBehaviorContext,
  GlobalNavBehaviorEnum
} from 'client/GlobalNavigation/GlobalNavBehavior.context';
import usePrevious from 'lib/hooks/usePrevious';

import './practice-view-wrapper.scss';

interface PracticeViewWrapperProps extends PropsWithChildrenRequired {
  activeQuestion: QuestionModelV3;
  questionRendererType: 'PreGuess' | 'PostGuess';
  studentId?: string;
  displayOrientation: 'default' | 'vertical' | 'horizontal';
  mainWrapperRef?: RefObject<HTMLDivElement>;
  answerInputPaneRef: RefObject<HTMLDivElement>;
  stackedContainerRef: RefObject<HTMLDivElement>;
  postSubmissionScrollPointRef: RefObject<HTMLDivElement>;
}

export const PracticeViewWrapper = ({
  activeQuestion,
  questionRendererType,
  studentId,
  displayOrientation,
  mainWrapperRef,
  answerInputPaneRef,
  stackedContainerRef,
  postSubmissionScrollPointRef,
  children
}: PracticeViewWrapperProps) => {
  const previousQuestionId = usePrevious(activeQuestion && activeQuestion.getId());
  const previousStudentId = usePrevious(studentId);
  const previousRenderType = usePrevious(questionRendererType);

  const {navBehavior} = useContext(GlobalNavBehaviorContext);

  useEffect(() => {
    callAction(appActions.SET_FOOTER_DISPLAY, false);
    copyToClipboard.preventCopy();
    return () => {
      callAction(appActions.SET_FOOTER_DISPLAY, true);
      copyToClipboard.allowCopy();
    };
  }, []);

  useEffect(() => {
    // allow copying for free response questions
    if (activeQuestion && activeQuestion.getQuestionType() === QUESTION_TYPES.FREE_RESPONSE.name) {
      copyToClipboard.allowCopy();
    } else {
      copyToClipboard.preventCopy();
    }
  }, [activeQuestion]);

  const updateScrollPosition = useCallback(() => {
    if (!mainWrapperRef) {
      return;
    }
    // checking for changes to determine if we should trigger auto scroll behavior
    const isQuestionChanging = previousQuestionId !== (activeQuestion && activeQuestion.getId());
    const isStudentChanging = previousStudentId !== studentId;
    // we check if studentId exists or not because in ViewingStudentSubmission.tsx, a teacher can go from viewing a
    // student who didn't answer (PreGuess) to one who did which would trigger scroll behavior. By checking for if
    // studentId exists, we prevent getIsRendererTypeChanging from returning true which prevents scroll behavior
    const isRendererTypeChanging = previousRenderType !== questionRendererType && !studentId;

    const shouldScroll = isQuestionChanging || isStudentChanging || isRendererTypeChanging;
    if (!shouldScroll) {
      return;
    }

    // If we're going from one question to another, or if we're going from the PostGuess to the PreGuess state, we snap back to the top
    const shouldScrollToTop =
      isQuestionChanging || isStudentChanging || questionRendererType === 'PreGuess';
    if (shouldScrollToTop) {
      // Get appropriate scroll container based on orientation
      const scrollContainerMap = {
        vertical: answerInputPaneRef?.current,
        horizontal: stackedContainerRef?.current,
        default: mainWrapperRef?.current
      };

      scrollContainerMap[displayOrientation]?.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // Otherwise, we're going from the PreGuess to the PostGuess state, so we scroll to the explanation
      postSubmissionScrollPointRef.current?.scrollIntoView({
        block: 'start',
        behavior: 'smooth'
      });
    }
  }, [
    previousQuestionId,
    previousStudentId,
    previousRenderType,
    questionRendererType,
    mainWrapperRef,
    activeQuestion,
    studentId,
    displayOrientation,
    answerInputPaneRef,
    stackedContainerRef,
    postSubmissionScrollPointRef
  ]);

  useEffect(() => {
    updateScrollPosition();
  }, [updateScrollPosition]);

  return (
    <div
      className={cx('u-display_flex u-flex-direction_column u-overflow_auto', {
        'practice-view-wrapper--without-global-nav':
          navBehavior === GlobalNavBehaviorEnum.NOT_VISIBLE,
        'practice-view-wrapper': navBehavior === GlobalNavBehaviorEnum.VISIBLE_STICKY
      })}
      data-testid='practice-view__container'
    >
      {children}
    </div>
  );
};
